import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiClipboard } from "react-icons/fi";
import { EmptyState } from "components/ui";

type dataKeysType = {
  id: number;
  column: string;
  label: string;
};

export type ThElementsType = {
  scope: string;
  classes: string;
  dataKeys: dataKeysType[];
};

/** Data keys rendered as right-aligned, tabular numbers. */
const DEFAULT_NUMERIC_LABELS = ["points", "finalPoint", "loi"];

const MySurveysTable = ({
  thElements,
  tdElements,
  searchQuery,
  tdDataStyle,
  tdRowStyle,
  tableStyle,
  startIndex = 0,
  numericLabels = DEFAULT_NUMERIC_LABELS,
}: {
  thElements: ThElementsType;
  tdElements: any;
  searchQuery: string;
  tdDataStyle: string;
  tdRowStyle: string;
  /** Applied to the table wrapper (e.g. responsive visibility). */
  tableStyle: string;
  startIndex?: number;
  numericLabels?: string[];
}) => {
  const { t } = useTranslation();
  const [ThElementsList, setThElementsList] = useState([]);
  const [TbodyElements, setTbodyElements] = useState([]);

  const cellTone = (label: string) => {
    if (numericLabels.includes(label)) return "is-num";
    if (label === "studyName") return "is-strong max-w-[320px] truncate";
    return "";
  };

  useEffect(() => {
    if (thElements) {
      setThElementsList([
        <th key="sno" scope="col" className={`w-16 ${thElements.classes}`}>
          S.No
        </th>,
        ...thElements.dataKeys.map((element: dataKeysType) => (
          <th
            key={element.id}
            scope={thElements.scope}
            className={`${thElements.classes} ${numericLabels.includes(element.label) ? "is-num" : ""}`}
          >
            {element.column}
          </th>
        )),
      ]);
    }

    if (tdElements && tdElements?.length) {
      if (TbodyElements.length > 0) {
        setTbodyElements([]);
      }
      setTbodyElements(
        tdElements?.map((element, index) => {
          return (
            <tr key={element.id} className={tdRowStyle}>
              <td className={`tabular-nums text-ink-3 ${tdDataStyle}`}>{startIndex + index + 1}</td>
              {thElements?.dataKeys.map((e) => {
                return (
                  <td key={`${element._id}-${e.label}`} className={`${cellTone(e.label)} ${tdDataStyle}`}>
                    {element[e.label]}
                  </td>
                );
              })}
            </tr>
          );
        })
      );
    }
  }, [thElements, tdElements, searchQuery, startIndex]);

  return (
    <div className={tableStyle}>
      <div className="zs-table-wrap is-flush">
        <table className="zs-table">
          <thead>
            <tr>{ThElementsList}</tr>
          </thead>
          <tbody>{tdElements?.length ? TbodyElements : null}</tbody>
        </table>
      </div>
      {!tdElements?.length && (
        <EmptyState
          icon={<FiClipboard />}
          title={t("page.mysurvey.nodata")}
          description="Records will appear here as soon as they are available."
        />
      )}
    </div>
  );
};

export default memo(MySurveysTable);
